import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { crmApi, errMsg } from '@crm/api';
import { ClientStatusSelect } from '@crm/components/ClientStatusSelect';
import { UiAutosuggest, UiButton } from '@shared/common/ui';

/**
 * Массовая смена статуса участий: направление + текущий status → новый status.
 */
export function ClientsMassStatusModal({ open, onClose, statuses = [] }) {
  const { branchId } = useParams();
  const [dirQuery, setDirQuery] = useState('');
  const [dirSuggestions, setDirSuggestions] = useState([]);
  const [directionId, setDirectionId] = useState('');
  const [directionLabel, setDirectionLabel] = useState('');
  const [fromStatus, setFromStatus] = useState('');
  const [toStatus, setToStatus] = useState('');
  const [previewCount, setPreviewCount] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [doneMsg, setDoneMsg] = useState(null);

  useEffect(() => {
    if (!open) return;
    setDirQuery('');
    setDirSuggestions([]);
    setDirectionId('');
    setDirectionLabel('');
    setFromStatus('');
    setToStatus('');
    setPreviewCount(null);
    setError(null);
    setDoneMsg(null);
  }, [open]);

  useEffect(() => {
    if (!open || !branchId || dirQuery.trim().length < 2 || directionId) {
      setDirSuggestions([]);
      return undefined;
    }
    let alive = true;
    const t = setTimeout(async () => {
      try {
        const { data } = await crmApi(branchId).get('/directions', {
          params: { q: dirQuery.trim(), limit: 30 },
        });
        if (alive) setDirSuggestions(data.items ?? []);
      } catch {
        if (alive) setDirSuggestions([]);
      }
    }, 250);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [open, branchId, dirQuery, directionId]);

  if (!open) return null;

  const run = async (apply) => {
    setError(null);
    setDoneMsg(null);
    if (!directionId || !fromStatus || !toStatus) {
      setError('Укажите направление, текущий и новый статус');
      return;
    }
    setBusy(true);
    try {
      const { data } = await crmApi(branchId).post('/clients/directions/mass-status', {
        directionId,
        fromStatus,
        toStatus,
        apply,
      });
      setPreviewCount(Number(data.count ?? 0));
      if (apply) {
        setDoneMsg(`Обновлено участий: ${data.updated ?? 0}`);
      }
    } catch (e) {
      setError(errMsg(e, 'Не удалось выполнить массовую смену'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="modal-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mass-status-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="modal-card__header">
          <h2 id="mass-status-title">Массовая смена статуса</h2>
          <UiButton variant="ghost" size="sm" type="button" onClick={onClose} aria-label="Закрыть">
            ×
          </UiButton>
        </header>
        <div className="modal-card__body" style={{ display: 'grid', gap: 12 }}>
          <p className="muted" style={{ margin: 0 }}>
            Для текущего филиала: все активные участия с выбранным направлением и статусом получат
            новый статус. Каждая смена пишется в историю.
          </p>

          <UiAutosuggest
            className="field"
            label="Направление"
            value={directionId ? directionLabel : dirQuery}
            onChange={(value) => {
              if (directionId) {
                setDirectionId('');
                setDirectionLabel('');
                setPreviewCount(null);
              }
              setDirQuery(value);
            }}
            suggestions={dirSuggestions}
            onSelect={(item) => {
              setDirectionId(item.id);
              setDirectionLabel(item.name ?? item.id);
              setDirQuery('');
              setDirSuggestions([]);
              setPreviewCount(null);
            }}
            onClear={() => {
              setDirectionId('');
              setDirectionLabel('');
              setDirQuery('');
              setPreviewCount(null);
            }}
            placeholder="Начните вводить название…"
            getOptionKey={(item) => item.id}
            getOptionLabel={(item) => item.name}
          />

          <div className="field">
            <label>Текущий статус</label>
            <ClientStatusSelect
              value={fromStatus}
              onChange={(v) => {
                setFromStatus(v);
                setPreviewCount(null);
              }}
              statuses={statuses}
              emptyLabel="Выберите"
            />
          </div>

          <div className="field">
            <label>Новый статус</label>
            <ClientStatusSelect
              value={toStatus}
              onChange={(v) => {
                setToStatus(v);
                setPreviewCount(null);
              }}
              statuses={statuses}
              emptyLabel="Выберите"
            />
          </div>

          {previewCount !== null ? (
            <div className="banner">
              Найдено участий: <strong>{previewCount}</strong>
            </div>
          ) : null}
          {doneMsg ? <div className="banner banner--success">{doneMsg}</div> : null}
          {error ? <div className="form-error">{error}</div> : null}

          <div className="form-actions">
            <UiButton type="button" variant="ghost" disabled={busy} onClick={() => run(false)}>
              Предпросмотр
            </UiButton>
            <UiButton
              type="button"
              disabled={busy || previewCount === null || previewCount === 0}
              onClick={() => {
                if (
                  !window.confirm(
                    `Применить новый статус к ${previewCount} участиям в текущем филиале?`,
                  )
                ) {
                  return;
                }
                run(true);
              }}
            >
              Подтвердить
            </UiButton>
          </div>
        </div>
      </div>
    </div>
  );
}
